import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Table,
  Input,
  Select,
  Tag,
  Button,
  Modal,
  message,
  Dropdown,
  Menu,
  Tooltip,
  DatePicker,
  Skeleton,
} from "antd";
import {
  hasPermission,
  privilegesEnum,
} from "../../../helpers/PermissionHelper";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../../helpers/FilterSearchHelper";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
  ClearOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import { RxSketchLogo } from "react-icons/rx";
import {
  deleteReward,
  getRewards,
  getRewardTypes,
} from "../../../api/Services";
import PageHeader from "../../../components/pageheader/PageHeader";
import EmptyState from "../../../components/emptystate/EmptyState";
import {
  getRewardsBasePath,
  getRewardTypesBasePath,
} from "../../../helpers/RewardsPathHelper";
import { COMMERCIAL_INTELLIGENCE_PATHS } from "../../../helpers/MenuPathHelper";
import styles from "./Rewards.module.scss";
import moment from "moment";

const { RangePicker } = DatePicker;

const SKELETON_ROW_COUNT = 10;
const SKELETON_CELL_WIDTHS = [
  "90%",
  "80%",
  "60%",
  "50%",
  "70%",
  "55%",
  "75%",
  "50%",
  "70%",
  "65%",
  "50%",
  "24px",
];

const RewardsTableSkeleton = () => (
  <div className={styles.tableSkeleton}>
    <div className={styles.tableSkeletonHeader}>
      {SKELETON_CELL_WIDTHS.map((width, index) => (
        <div key={`header-${index}`} className={styles.tableSkeletonCell}>
          <Skeleton.Input active size="small" style={{ width, height: 14 }} />
        </div>
      ))}
    </div>
    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
      <div key={`row-${rowIndex}`} className={styles.tableSkeletonRow}>
        {SKELETON_CELL_WIDTHS.map((width, cellIndex) => (
          <div
            key={`cell-${rowIndex}-${cellIndex}`}
            className={styles.tableSkeletonCell}
          >
            <Skeleton.Input
              active
              size="small"
              style={{
                width: cellIndex === 2 ? "56px" : width,
                height: cellIndex === 2 ? 22 : 16,
                borderRadius: cellIndex === 2 ? 12 : 4,
              }}
            />
          </div>
        ))}
      </div>
    ))}
  </div>
);

const Rewards = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const basePath = getRewardsBasePath(location.pathname);
  const rewardTypesBasePath = getRewardTypesBasePath(location.pathname);

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(
    parseInt(searchParams.get("page") || "1"),
  );
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();
  const [nameFilterValue, setNameFilterValue] = useState(
    searchParams.get("name") || "",
  );
  const debouncedNameFilterValue = useDebouncedValue(nameFilterValue);
  const [activeFilterValue, setActiveFilterValue] = useState<
    string | undefined
  >(searchParams.get("active") || undefined);
  const [dateRangeFilterValue, setDateRangeFilterValue] = useState<
    [moment.Moment, moment.Moment] | null
  >(
    searchParams.get("dateStart") && searchParams.get("dateEnd")
      ? [
          moment(searchParams.get("dateStart")),
          moment(searchParams.get("dateEnd")),
        ]
      : null,
  );
  const [typeFilterValue, setTypeFilterValue] = useState<number | undefined>(
    searchParams.get("type") ? parseInt(searchParams.get("type")!) : undefined,
  );
  const [originFilterValue, setOriginFilterValue] = useState<string | undefined>(
    searchParams.get("origin") || "SUGGESTED",
  );
  const [rewardTypes, setRewardTypes] = useState<any[]>([]);

  const updateURL = useCallback(
    (filters: {
      name?: string;
      active?: string | undefined;
      page?: number;
      dateStart?: string;
      dateEnd?: string;
      type?: number | undefined;
      origin?: string | undefined;
    }) => {
      const params = new URLSearchParams();

      if (filters.name) params.set("name", filters.name);
      if (filters.active !== undefined) params.set("active", filters.active);
      if (filters.page && filters.page > 1)
        params.set("page", filters.page.toString());
      if (filters.dateStart) params.set("dateStart", filters.dateStart);
      if (filters.dateEnd) params.set("dateEnd", filters.dateEnd);
      if (filters.type !== undefined)
        params.set("type", filters.type.toString());
      if (filters.origin) params.set("origin", filters.origin);

      setSearchParams(params);
    },
    [setSearchParams],
  );

  const fetchRewards = useCallback(
    async (pagination = actualPage) => {
      const requestId = beginRequest();
      setLoading(true);
      try {
        const response = await getRewards(pagination, {
          name: debouncedNameFilterValue,
          active: activeFilterValue ? activeFilterValue === "true" : undefined,
          dateStart: dateRangeFilterValue
            ? dateRangeFilterValue[0]?.format("YYYY-MM-DD")
            : undefined,
          dateEnd: dateRangeFilterValue
            ? dateRangeFilterValue[1]?.format("YYYY-MM-DD")
            : undefined,
          type: typeFilterValue,
          origin: originFilterValue,
        });

        if (isStaleRequest(requestId)) return;

        setData(response.rewards ?? []);
        setTotalSize(response.totalElements ?? 0);
      } catch (e) {
        if (isStaleRequest(requestId)) return;
        message.error(t("Common-ErrorMessage"));
      } finally {
        if (!isStaleRequest(requestId)) {
          setLoading(false);
        }
      }
    },
    [
      actualPage,
      debouncedNameFilterValue,
      activeFilterValue,
      dateRangeFilterValue,
      typeFilterValue,
      originFilterValue,
      beginRequest,
      isStaleRequest,
      t,
    ],
  );

  const handleSearchRewardTypes = async (value: string) => {
    const response = await getRewardTypes(1, { name: value });
    setRewardTypes(response.rewardTypes);
  };

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_REWARDS)) {
      fetchRewards();
    }
  }, [fetchRewards]);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_REWARDS)) {
      handleSearchRewardTypes("");
    }
  }, []);

  const showDeleteConfirm = (id: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(id);
      },
    });
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    const deleteStatus = await deleteReward(id);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await fetchRewards();
    setLoading(false);
  };

  const handlePagination = (page: number) => {
    setActualPage(page);
    updateURL({
      name: nameFilterValue,
      active: activeFilterValue,
      page,
      origin: originFilterValue,
      type: typeFilterValue,
      dateStart: dateRangeFilterValue
        ? dateRangeFilterValue[0]?.format("YYYY-MM-DD")
        : undefined,
      dateEnd: dateRangeFilterValue
        ? dateRangeFilterValue[1]?.format("YYYY-MM-DD")
        : undefined,
    });
  };

  const handleClearFilters = () => {
    setNameFilterValue("");
    setActiveFilterValue(undefined);
    setDateRangeFilterValue(null);
    setTypeFilterValue(undefined);
    setOriginFilterValue("SUGGESTED");
    setActualPage(1);
    updateURL({ origin: "SUGGESTED", page: 1 });
  };

  const hasActiveFilters = () => {
    return (
      nameFilterValue !== "" ||
      activeFilterValue !== undefined ||
      dateRangeFilterValue !== null ||
      typeFilterValue !== undefined ||
      originFilterValue !== undefined
    );
  };

  const hasNoData = () => {
    return !loading && data.length === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return <EmptyState type="noResults" entityName="Incentivo" />;
    }

    if (hasNoDataAtAll()) {
      return (
        <EmptyState
          type="noData"
          entityName="Incentivo"
          createPath={`${basePath}/create`}
          createPermission={privilegesEnum.WRITE_REWARDS}
        />
      );
    }

    return null;
  };

  const getRewardViewUrl = (id: number) => {
    const params = new URLSearchParams();
    if (nameFilterValue) params.set("name", nameFilterValue);
    if (activeFilterValue !== undefined)
      params.set("active", activeFilterValue.toString());
    if (actualPage > 1) params.set("page", actualPage.toString());
    if (dateRangeFilterValue) {
      params.set("dateStart", dateRangeFilterValue[0]?.format("YYYY-MM-DD"));
      params.set("dateEnd", dateRangeFilterValue[1]?.format("YYYY-MM-DD"));
    }
    if (typeFilterValue !== undefined)
      params.set("type", typeFilterValue.toString());
    if (originFilterValue) params.set("origin", originFilterValue);

    const queryString = params.toString();
    return `${basePath}/${id}/view${queryString ? `?${queryString}` : ""}`;
  };

  const getOptions = (items: any) => {
    const options: any[] = [];
    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>,
      );
    });
    return options;
  };

  const columns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      render: (name: string, record: any) => (
        <a href={getRewardViewUrl(record.id)} className={styles.nameLink}>
          {name}
        </a>
      ),
    },
    {
      title: t("Common-Type"),
      dataIndex: "type",
      render: (type: any) => (
        <a
          href={`${rewardTypesBasePath}/${type.id}/view`}
          className={styles.nameLink}
        >
          {type.name}
        </a>
      ),
    },
    {
      title: t("Common-Status"),
      dataIndex: "status",
      render: (status: boolean) => (
        <Tag color={status ? "green" : "red"}>
          {status ? t("Common-Active") : t("Common-Inactive")}
        </Tag>
      ),
    },
    {
      title: t("Common-Value"),
      key: "incentiveValue",
      render: (_: unknown, record: any) => {
        if (record.pointsValue != null && record.pointsValue !== 0) {
          return `${record.pointsValue} pts`;
        }
        if (record.value != null && record.value !== 0) {
          return `${record.value}%`;
        }
        return "-";
      },
    },
    {
      title: t("Common-CreatedDate"),
      dataIndex: "createdAt",
      render: (date: any) =>
        date ? moment(date, "YYYY-MM-DD").format("DD-MM-YYYY") : "",
    },
    {
      title: t("Common-EstimatedReach"),
      key: "reach",
      render: (_: unknown, record: any) => {
        const value =
          record.clientCount != null && record.clientCount > 0
            ? record.clientCount
            : record.estimatedReach;
        return value === null || value === undefined ? "-" : `${value}`;
      },
    },
    {
      title: t("Common-EstimatedImpact"),
      dataIndex: "estimatedImpact",
      render: (value: number) =>
        value === null || value === undefined ? "-" : `$${value}`,
    },
    {
      title: t("Common-AccumulatedImpact"),
      dataIndex: "accumulatedImpact",
      render: (value: number) =>
        value === null || value === undefined ? "-" : `$${value}`,
    },
    {
      title: t("Common-AccumulatedCost"),
      dataIndex: "accumulatedCost",
      render: (value: number) =>
        value === null || value === undefined ? "-" : `$${value}`,
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => navigate(getRewardViewUrl(record.id))}
              >
                {t("Common-View")}
              </Menu.Item>
              {hasPermission(privilegesEnum.WRITE_REWARDS) && (
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => navigate(getRewardViewUrl(record.id))}
                >
                  {t("Common-Edit")}
                </Menu.Item>
              )}
              {hasPermission(privilegesEnum.DELETE_REWARDS) && (
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteConfirm(record.id)}
                >
                  {t("Common-Delete")}
                </Menu.Item>
              )}

              <Menu.Item
                key="createCampaign"
                icon={<NotificationOutlined />}
                onClick={() =>
                  navigate(`${COMMERCIAL_INTELLIGENCE_PATHS.campaigns}/create`, {
                    state: {
                      rewardId: record.id,
                      rewardName: record.name,
                      clientCount: record.clientCount,
                      estimatedImpact: record.estimatedImpact,
                      estimatedReach: record.estimatedReach,
                      estimatedRoi: record.estimatedRoi,
                    },
                  })
                }
              >
                {t("Common-NewCampaign")}
              </Menu.Item>
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("NavBar-Rewards")}
        icon={<RxSketchLogo />}
        onCreateClick={() => navigate(`${basePath}/create`)}
        createPermission={privilegesEnum.WRITE_REWARDS}
      />
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <div className={styles.headerTableWrapper}>
            <div className={styles.filtersWrapper}>
              <div className={styles.filterWrapper}>
                <Input
                  placeholder={t("Common-SearchByName")}
                  className={styles.filterInput}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => {
                    setNameFilterValue(e.target.value);
                    setActualPage(1);
                    updateURL({
                      name: e.target.value,
                      active: activeFilterValue,
                      type: typeFilterValue,
                      origin: originFilterValue,
                      page: 1,
                    });
                  }}
                  value={nameFilterValue}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  showSearch
                  allowClear
                  value={activeFilterValue}
                  className={styles.filterSelect}
                  placeholder={t("Common-SearchByState")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={(_, option) => {
                    if (option) {
                      setActiveFilterValue(option.value);
                      setActualPage(1);
                      updateURL({
                        name: nameFilterValue,
                        active: option.value,
                        type: typeFilterValue,
                        origin: originFilterValue,
                        page: 1,
                      });
                    }
                  }}
                  onClear={() => {
                    setActiveFilterValue(undefined);
                    setActualPage(1);
                    updateURL({
                      name: nameFilterValue,
                      active: undefined,
                      type: typeFilterValue,
                      origin: originFilterValue,
                      page: 1,
                    });
                  }}
                >
                  <Select.Option value="false">
                    {t("Common-Inactive")}
                  </Select.Option>
                  <Select.Option value="true">
                    {t("Common-Active")}
                  </Select.Option>
                </Select>
              </div>
              <div className={styles.filterWrapper}>
                <RangePicker
                  className={styles.filterInput}
                  value={dateRangeFilterValue}
                  onChange={(dates) => {
                    setDateRangeFilterValue(
                      dates as [moment.Moment, moment.Moment] | null,
                    );
                    setActualPage(1);
                    updateURL({
                      name: nameFilterValue,
                      active: activeFilterValue,
                      page: 1,
                      type: typeFilterValue,
                      origin: originFilterValue,
                      dateStart: dates
                        ? dates[0]?.format("YYYY-MM-DD")
                        : undefined,
                      dateEnd: dates
                        ? dates[1]?.format("YYYY-MM-DD")
                        : undefined,
                    });
                  }}
                  placeholder={[t("Common-StartDate"), t("Common-EndDate")]}
                />
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  showSearch
                  allowClear
                  value={typeFilterValue}
                  className={styles.filterSelect}
                  placeholder={t("Common-Type")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSearch={handleSearchRewardTypes}
                  onSelect={(_, option) => {
                    if (option) {
                      setTypeFilterValue(option.value);
                      setActualPage(1);
                      updateURL({
                        name: nameFilterValue,
                        active: activeFilterValue,
                        type: option.value,
                        origin: originFilterValue,
                        page: 1,
                      });
                    }
                  }}
                  onClear={() => {
                    setTypeFilterValue(undefined);
                    setActualPage(1);
                    updateURL({
                      name: nameFilterValue,
                      active: activeFilterValue,
                      type: undefined,
                      origin: originFilterValue,
                      page: 1,
                    });
                  }}
                >
                  {getOptions(rewardTypes)}
                </Select>
              </div>
              <div className={styles.filterWrapper}>
                <Select
                  allowClear
                  value={originFilterValue}
                  className={styles.filterSelect}
                  placeholder={t("Common-Origin")}
                  onSelect={(_, option) => {
                    if (option) {
                      setOriginFilterValue(option.value);
                      setActualPage(1);
                      updateURL({
                        name: nameFilterValue,
                        active: activeFilterValue,
                        type: typeFilterValue,
                        origin: option.value,
                        page: 1,
                      });
                    }
                  }}
                  onClear={() => {
                    setOriginFilterValue(undefined);
                    setActualPage(1);
                    updateURL({
                      name: nameFilterValue,
                      active: activeFilterValue,
                      type: typeFilterValue,
                      origin: undefined,
                      page: 1,
                    });
                  }}
                >
                  <Select.Option value="MANUAL">
                    {t("Common-Manual")}
                  </Select.Option>
                  <Select.Option value="SUGGESTED">
                    {t("Common-Suggested")}
                  </Select.Option>
                </Select>
              </div>
              {hasActiveFilters() && (
                <div className={styles.filterWrapper}>
                  <Tooltip title={t("Common-Clear")}>
                    <Button
                      onClick={handleClearFilters}
                      className={styles.clearButton}
                      icon={<ClearOutlined />}
                    >
                      {t("Common-Clear")}
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <RewardsTableSkeleton />
          ) : (
            <Table
              size="small"
              className={styles.table}
              rowKey={(record) => record.id}
              columns={columns}
              dataSource={data}
              pagination={{
                total: totalSize,
                showSizeChanger: false,
                onChange: handlePagination,
                current: actualPage,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} / ${total}`,
              }}
              locale={{
                emptyText: getEmptyState(),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Rewards;
